import { createBrowserRouter, Navigate } from 'react-router';
import { RequireAuth } from './auth/RequireAuth';
import { ApprovalThresholdsPage } from './features/approval-thresholds/ApprovalThresholdsPage';
import { CompanySettingsPage } from './features/company-settings/CompanySettingsPage';
import { ComplianceOverviewPage } from './features/compliance/ComplianceOverviewPage';
import { CUSTOMER_TABS } from './features/customers/customer-tabs';
import { CustomerDetailPage } from './features/customers/CustomerDetailPage';
import { CustomersPage } from './features/customers/CustomersPage';
import { DRIVER_TABS } from './features/drivers/driver-tabs';
import { DriverDetailPage } from './features/drivers/DriverDetailPage';
import { DriversPage } from './features/drivers/DriversPage';
import { FLEET_TABS, fleetTabPath } from './features/fleet/fleet-tabs';
import { FleetPage } from './features/fleet/FleetPage';
import { RedirectToFleetRecord } from './features/fleet/RedirectToFleetRecord';
import { JobIntakePage } from './features/jobs/JobIntakePage';
import { OWNER_TABS } from './features/owners/owner-tabs';
import { OwnerDetailPage } from './features/owners/OwnerDetailPage';
import { REFERENCE_DATA_TABS, referenceDataTabPath } from './features/reference-data/reference-data-tabs';
import { ReferenceDataPage } from './features/reference-data/ReferenceDataPage';
import { ZoneEditorPage } from './features/reference-data/ZoneEditorPage';
import { RolesPage } from './features/roles/RolesPage';
import { SecurityPage } from './features/security/SecurityPage';
import { TRAILER_TABS } from './features/trailers/trailer-tabs';
import { TrailerDetailPage } from './features/trailers/TrailerDetailPage';
import { TRUCK_TABS } from './features/trucks/truck-tabs';
import { TruckDetailPage } from './features/trucks/TruckDetailPage';
import { USER_TABS } from './features/users/user-tabs';
import { UserDetailPage } from './features/users/UserDetailPage';
import { UsersPage } from './features/users/UsersPage';
import { AppLayout } from './layouts/AppLayout';
import { OFFICE_SECTIONS } from './layouts/office-sections';
import { recordTabRoutes } from './lib/record-tab-routes';
import { YardLayout } from './layouts/YardLayout';
import { LoginPage } from './pages/LoginPage';
import { NotFound } from './pages/NotFound';
import { SectionPage } from './pages/SectionPage';
import { YardTasksPage } from './pages/YardTasksPage';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        path: '/',
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/dispatch" replace /> },
          { path: 'account/security', element: <SecurityPage /> },
          { path: 'roles', element: <RolesPage /> },
          { path: 'users', element: <UsersPage /> },
          ...recordTabRoutes('users/:id', USER_TABS, (tab) => <UserDetailPage tab={tab} />),
          { path: 'approval-thresholds', element: <ApprovalThresholdsPage /> },
          { path: 'reference-data', element: <Navigate to={referenceDataTabPath(REFERENCE_DATA_TABS[0])} replace /> },
          ...REFERENCE_DATA_TABS.map((tab) => ({ path: `reference-data/${tab}`, element: <ReferenceDataPage tab={tab} /> })),
          { path: 'reference-data/zones/new', element: <ZoneEditorPage /> },
          { path: 'reference-data/zones/:id', element: <ZoneEditorPage /> },
          { path: 'company', element: <CompanySettingsPage /> },
          { path: 'jobs', element: <JobIntakePage /> },
          { path: 'jobs/:id', element: <JobIntakePage /> },
          { path: 'customers', element: <CustomersPage /> },
          ...recordTabRoutes('customers/:id', CUSTOMER_TABS, (tab) => <CustomerDetailPage tab={tab} />),
          { path: 'fleet', element: <Navigate to={fleetTabPath(FLEET_TABS[0])} replace /> },
          ...FLEET_TABS.map((tab) => ({ path: `fleet/${tab}`, element: <FleetPage tab={tab} /> })),
          ...recordTabRoutes('fleet/trucks/:id', TRUCK_TABS, (tab) => <TruckDetailPage tab={tab} />),
          ...recordTabRoutes('fleet/trailers/:id', TRAILER_TABS, (tab) => <TrailerDetailPage tab={tab} />),
          ...recordTabRoutes('fleet/owners/:id', OWNER_TABS, (tab) => <OwnerDetailPage tab={tab} />),
          { path: 'fleet/:id', element: <RedirectToFleetRecord tab="trucks" /> },
          { path: 'trailers/:id', element: <RedirectToFleetRecord tab="trailers" /> },
          { path: 'owners/:id', element: <RedirectToFleetRecord tab="owners" /> },
          { path: 'drivers', element: <DriversPage /> },
          ...recordTabRoutes('drivers/:id', DRIVER_TABS, (tab) => <DriverDetailPage tab={tab} />),
          { path: 'compliance', element: <ComplianceOverviewPage /> },
          ...OFFICE_SECTIONS.filter(
            (section) =>
              !['roles', 'users', 'approval-thresholds', 'reference-data', 'company', 'jobs', 'customers', 'fleet', 'drivers', 'compliance'].includes(
                section.slug,
              ),
          ).map((section) => ({
            path: section.slug,
            element: <SectionPage section={section} />,
          })),
        ],
      },
      {
        path: '/yard',
        element: <YardLayout />,
        children: [{ index: true, element: <YardTasksPage /> }],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
]);
